/**
 * Cost & AFE **write layer** — RTK Query endpoints injected onto the shared
 * `pApi` (Oxbo-P client) so the daily-report Cost tab can view + edit + add the
 * day's cost actual lines, and so the AFE-entry surface can add/edit AFE PLAN
 * lines. The shared contract files (`@api/pApi`, `@api/types`) stay untouched:
 * all request/response types are declared locally here and every hook is
 * generated by `pApi.injectEndpoints`, keeping branches merge-clean.
 *
 * API contract (Oxbo-P, `/v1`, JWT; PUT/PATCH key on `entity_id`, not `id`):
 *   - Cost actual lines (daily_report-scoped, all event types):
 *       GET  /reports/{report_id}/costs                 → CostLineOut[]
 *       POST /reports/{report_id}/costs                 → CostLineOut
 *       PUT  /reports/{report_id}/costs/{entity_id}     → CostLineOut
 *   - AFE PLAN lines (event-scoped, existing/unchanged):
 *       POST  /events/{event_id}/afe-lines              → AfeLineOut
 *       PATCH /events/{event_id}/afe-lines/{entity_id}  → AfeLineOut
 *
 * A write invalidates the relevant list so the panel refetches on success.
 */
import { pApi } from "@api/pApi";
import type { AfeLineOut } from "@api/types";

// ── Cost actual lines (daily_report-scoped) ────────────────────────────────

/**
 * CostLineOut — one daily cost actual line. Mirrors the Oxbo-P CostLineOut wire
 * shape (the POST/PUT body fields + the server-assigned identity/scope fields).
 * Money/qty are floats as-entered; `*_entry_key` fields carry picklist keys.
 */
export interface CostLineOut {
  id: string;
  /** Stable logical line id — key the PUT on this, not `id`. */
  entity_id: string;
  version: number;
  event_id: string | null;
  daily_report_id: string | null;
  well_uwi: string;
  sequence_no: number | null;
  cost_date: string | null;
  cost_class_entry_key: string | null;
  cost_code_entry_key: string | null;
  phase_entry_key: string | null;
  afe_ref: string | null;
  afe_line_entity_id: string | null;
  item_description: string | null;
  vendor_entry_key: string | null;
  qty: number | null;
  unit_entry_key: string | null;
  unit_price: number | null;
  total: number | null;
  currency: string | null;
  carry_over: boolean | null;
  remarks: string | null;
}

/** POST /reports/{report_id}/costs body. `well_uwi` is required on create. */
export interface CostLineCreate {
  well_uwi: string;
  sequence_no?: number | null;
  cost_date?: string | null;
  cost_class_entry_key?: string | null;
  cost_code_entry_key?: string | null;
  phase_entry_key?: string | null;
  afe_ref?: string | null;
  afe_line_entity_id?: string | null;
  item_description?: string | null;
  vendor_entry_key?: string | null;
  qty?: number | null;
  unit_entry_key?: string | null;
  unit_price?: number | null;
  total?: number | null;
  currency?: string | null;
  carry_over?: boolean | null;
  remarks?: string | null;
  change_reason?: string | null;
}

/** PUT /reports/{report_id}/costs/{entity_id} body — same fields, all optional. */
export type CostLineUpdate = Partial<CostLineCreate>;

// ── AFE PLAN lines (event-scoped, existing endpoints) ──────────────────────

/**
 * AfeLinePlanCreate — POST /events/{event_id}/afe-lines body. The AFE-entry
 * surface for "Maniobras y Códigos de costo": a plan/budget line keyed by its
 * cost_code + maneuver/phase, with the authorized amount. `afe_ref` groups the
 * line under an AFE; the server assigns the entity_id + version.
 */
export interface AfeLinePlanCreate {
  well_uwi: string;
  afe_ref: string;
  sequence_no?: number | null;
  cost_class_entry_key?: string | null;
  cost_code_entry_key?: string | null;
  phase_entry_key?: string | null;
  activity_code_entry_key?: string | null;
  vendor_entry_key?: string | null;
  item_description?: string | null;
  program_step?: number | null;
  accrual_method?: string | null;
  afe_amount?: number | null;
  item_quantity?: number | null;
  item_unit_entry_key?: string | null;
  item_cost?: number | null;
  currency?: string | null;
  change_reason?: string | null;
}

/** PATCH /events/{event_id}/afe-lines/{entity_id} body — same fields, optional. */
export type AfeLinePlanUpdate = Partial<AfeLinePlanCreate>;

export const costEntryApi = pApi.injectEndpoints({
  endpoints: (b) => ({
    // The day's cost actual lines (scoped to a daily report), server-ordered.
    getReportCosts: b.query<CostLineOut[], string>({
      query: (reportId) => `/reports/${reportId}/costs`,
      providesTags: (result, _e, reportId) =>
        result
          ? [
              ...result.map((c) => ({ type: "Report" as const, id: `COST-${c.entity_id}` })),
              { type: "Report" as const, id: `COSTS-${reportId}` },
            ]
          : [{ type: "Report" as const, id: `COSTS-${reportId}` }],
    }),
    createReportCost: b.mutation<CostLineOut, { reportId: string; body: CostLineCreate }>({
      query: ({ reportId, body }) => ({
        url: `/reports/${reportId}/costs`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { reportId }) => [{ type: "Report", id: `COSTS-${reportId}` }],
    }),
    updateReportCost: b.mutation<
      CostLineOut,
      { reportId: string; entityId: string; body: CostLineUpdate }
    >({
      query: ({ reportId, entityId, body }) => ({
        url: `/reports/${reportId}/costs/${entityId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { reportId }) => [{ type: "Report", id: `COSTS-${reportId}` }],
    }),

    // AFE PLAN lines — add/edit against the existing event-scoped endpoints.
    // Invalidate nothing via RTK tags (getAfeLines is untagged in pApi); the
    // AFE-entry surface refetches its list explicitly on success instead.
    createAfeLine: b.mutation<AfeLineOut, { eventId: string; body: AfeLinePlanCreate }>({
      query: ({ eventId, body }) => ({
        url: `/events/${eventId}/afe-lines`,
        method: "POST",
        body,
      }),
    }),
    updateAfeLine: b.mutation<
      AfeLineOut,
      { eventId: string; entityId: string; body: AfeLinePlanUpdate }
    >({
      query: ({ eventId, entityId, body }) => ({
        url: `/events/${eventId}/afe-lines/${entityId}`,
        method: "PATCH",
        body,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetReportCostsQuery,
  useCreateReportCostMutation,
  useUpdateReportCostMutation,
  useCreateAfeLineMutation,
  useUpdateAfeLineMutation,
} = costEntryApi;
